import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSprints } from '../../store/slices/sprintsSlice';
import { Avatar } from '../../components/common/Avatar';
import { TicketTypeIcon } from '../../components/common/TicketTypeSelector';
import api from '../../services/api';
import './AllWork.css';

const PRIORITY_CONFIG = {
  highest: { color: '#ff5630', label: 'Highest' },
  high:    { color: '#ff7452', label: 'High' },
  medium:  { color: '#ffab00', label: 'Medium' },
  low:     { color: '#2684ff', label: 'Low' },
  lowest:  { color: '#57d9a3', label: 'Lowest' },
};

const PriorityDot = ({ priority }) => {
  const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
  return (
    <span className="aw-priority-dot" style={{ background: cfg.color }} title={cfg.label} />
  );
};

const STATUS_FALLBACK_COLORS = {
  backlog:     '#6b7280',
  in_progress: '#3b82f6',
  review:      '#f59e0b',
  done:        '#10b981',
};

const StatusBadge = ({ status, workflowStatuses }) => {
  const ws = workflowStatuses.find(s => s.key === status);
  const label = ws?.name || status?.replace(/_/g, ' ') || '—';
  const color = ws?.color || STATUS_FALLBACK_COLORS[status] || '#6b7280';
  return (
    <span
      className="aw-status-badge"
      style={{ background: color + '22', color, borderColor: color + '55' }}
    >
      {label}
    </span>
  );
};

const PAGE_SIZE = 500;

const AllWork = ({ projectId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Local task state — fetched independently from the shared Redux tasks slice
  // so that navigating to AllWork doesn't clobber Backlog/Board task state.
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  const { sprints } = useSelector(s => s.sprints);
  const { currentProject } = useSelector(s => s.projects);

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterSprint, setFilterSprint] = useState('');
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, task: null });
  const contextMenuRef = useRef(null);

  useEffect(() => {
    if (!contextMenu.visible) return;
    const close = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu(m => ({ ...m, visible: false }));
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [contextMenu.visible]);

  const handleContextMenu = (e, task) => {
    e.preventDefault();
    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, task });
  };

  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [expandedParentIds, setExpandedParentIds] = useState(new Set());

  const workflowStatuses = useMemo(
    () => currentProject?.workflow?.statuses || [],
    [currentProject]
  );

  // Fetch every page until all tasks are loaded.
  const fetchAllTasks = useCallback(async () => {
    setLoading(true);
    try {
      let page = 1;
      let allTasks = [];
      while (true) {
        const res = await api.get(`/projects/${projectId}/tasks`, {
          params: { page, page_size: PAGE_SIZE },
        });
        const { tasks: pageTasks, total_pages } = res.data;
        allTasks = allTasks.concat(pageTasks || []);
        if (page >= total_pages) break;
        page += 1;
      }
      setTasks(allTasks);
    } catch (err) {
      console.error('AllWork: failed to load tasks', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchAllTasks();
    dispatch(fetchSprints({ projectId }));
  }, [dispatch, projectId, fetchAllTasks]);

  // Build sprint lookup
  const sprintMap = useMemo(() => {
    const m = {};
    sprints.forEach(s => { m[s.id] = s; });
    return m;
  }, [sprints]);

  // Unique assignees for filter dropdown
  const assignees = useMemo(() => {
    const seen = new Map();
    tasks.forEach(t => {
      if (t.assignee && !seen.has(t.assignee.id)) seen.set(t.assignee.id, t.assignee);
    });
    return Array.from(seen.values());
  }, [tasks]);

  const completedSprintIds = useMemo(
    () => new Set(sprints.filter(s => s.status === 'completed').map(s => s.id)),
    [sprints]
  );

  // Filter tasks — only tasks belonging to completed sprints
  const filteredTasks = useMemo(() => {
    return tasks.filter(t => {
      if (t.task_type === 'epic') return false;
      // Only show tasks in completed sprints
      if (!t.sprint_id || !completedSprintIds.has(t.sprint_id)) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!t.task_key?.toLowerCase().includes(q) && !t.title?.toLowerCase().includes(q)) return false;
      }
      if (filterType && t.task_type !== filterType) return false;
      if (filterAssignee && String(t.assignee?.id) !== filterAssignee) return false;
      if (filterSprint && String(t.sprint_id) !== filterSprint) return false;
      return true;
    });
  }, [tasks, search, filterType, filterAssignee, filterSprint, completedSprintIds]);

  // Build hierarchy: nest subtasks under their parent within each sprint group.
  // Parents without children are returned as-is; orphaned subtasks (parent not
  // in this sprint) are appended after the parent list.
  const buildHierarchy = (sprintTasks) => {
    const byId = {};
    sprintTasks.forEach(t => { byId[t.id] = { ...t, children: [] }; });

    const roots = [];
    const orphans = [];

    sprintTasks.forEach(t => {
      if (t.parent_id && byId[t.parent_id]) {
        byId[t.parent_id].children.push(byId[t.id]);
      } else if (t.parent_id && !byId[t.parent_id]) {
        orphans.push(byId[t.id]); // parent in a different sprint or epic
      } else {
        roots.push(byId[t.id]);
      }
    });

    // Flatten: parent row then its children (indented), then orphans + their children.
    // Orphans are tasks whose parent was filtered out (e.g. epics) or is in a
    // different sprint — treat them as depth-0 rows so their subtasks are also rendered.
    const flat = [];
    roots.forEach(r => {
      flat.push({ ...r, depth: 0 });
      r.children.forEach(c => flat.push({ ...c, depth: 1 }));
    });
    orphans.forEach(o => {
      flat.push({ ...o, depth: 0 });
      o.children.forEach(c => flat.push({ ...c, depth: 1 }));
    });
    return flat;
  };

  // Group tasks: completed sprints only, ordered by most recently completed (end_date desc)
  const groups = useMemo(() => {
    const completedSprints = [...sprints]
      .filter(s => s.status === 'completed')
      .sort((a, b) => {
        const aDate = a.end_date ? new Date(a.end_date) : new Date(0);
        const bDate = b.end_date ? new Date(b.end_date) : new Date(0);
        return bDate - aDate;
      });

    const result = completedSprints.map(sprint => {
      const sprintTasks = filteredTasks.filter(t => t.sprint_id === sprint.id);
      const flatHierarchy = buildHierarchy(sprintTasks);
      return {
        id: `sprint-${sprint.id}`,
        label: sprint.name,
        sublabel: 'Completed',
        status: sprint.status,
        start_date: sprint.start_date,
        end_date: sprint.end_date,
        tasks: flatHierarchy,
        taskCount: sprintTasks.length,
      };
    });

    return result.filter(g => g.taskCount > 0);
  }, [filteredTasks, sprints, filterSprint]);

  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => ({ ...prev, [groupId]: prev[groupId] === false }));
  };

  const toggleParent = (taskId) => {
    setExpandedParentIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) { next.delete(taskId); } else { next.add(taskId); }
      return next;
    });
  };

  const handleRowClick = (task) => {
    navigate(`/projects/${projectId}/board/${task.task_key}`);
  };

  const totalVisible = filteredTasks.length;

  if (loading && tasks.length === 0) {
    return (
      <div className="aw-loading">
        <span className="spinner" />
        <p>Loading work items…</p>
      </div>
    );
  }

  const allEmpty = groups.every(g => g.taskCount === 0);

  return (
    <div className="all-work">
      {/* Toolbar */}
      <div className="aw-toolbar">
        <div className="aw-search-wrap">
          <svg className="aw-search-icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
          </svg>
          <input
            className="aw-search"
            type="text"
            placeholder="Search by key or title…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="aw-search-clear" onClick={() => setSearch('')}>×</button>
          )}
        </div>

        <select className="aw-filter-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All types</option>
          <option value="story">Story</option>
          <option value="task">Task</option>
          <option value="bug">Bug</option>
          <option value="subtask">Subtask</option>
        </select>

        <select className="aw-filter-select" value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
          <option value="">All assignees</option>
          {assignees.map(a => (
            <option key={a.id} value={a.id}>{a.full_name}</option>
          ))}
        </select>

        <select className="aw-filter-select" value={filterSprint} onChange={e => setFilterSprint(e.target.value)}>
          <option value="">All completed sprints</option>
          {sprints.filter(s => s.status === 'completed').map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>

        <span className="aw-count">{totalVisible} issue{totalVisible !== 1 ? 's' : ''}</span>
      </div>

      {/* Content */}
      <div className="aw-content">
        {allEmpty ? (
          <div className="aw-no-results">No issues match the current filters.</div>
        ) : (
          groups.map(group => {
            const isCollapsed = collapsedGroups[group.id] !== false;
            const isExpanded = !isCollapsed;

            return (
              <div
                key={group.id}
                className={`aw-group${group.status === 'active' ? ' aw-group--active' : ''}${group.status === 'completed' ? ' aw-group--completed' : ''}`}
              >
                {/* Group header */}
                <div
                  className={`aw-group-header${isExpanded ? ' expanded' : ''}`}
                  onClick={() => toggleGroup(group.id)}
                >
                  <div className="aw-group-header-left">
                    <span
                      className="aw-group-chevron"
                      style={{ transform: isExpanded ? 'rotate(90deg)' : 'none' }}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                      </svg>
                    </span>
                    <span className="aw-group-label">{group.label}</span>
                    {group.sublabel && (
                      <span className={`aw-sprint-badge aw-sprint-badge--${group.status}`}>
                        {group.sublabel}
                      </span>
                    )}
                    {(group.start_date || group.end_date) && (
                      <span className="aw-sprint-dates">
                        {group.start_date ? format(parseISO(group.start_date), 'MMM d, yyyy') : '?'}
                        {' – '}
                        {group.end_date ? format(parseISO(group.end_date), 'MMM d, yyyy') : '?'}
                      </span>
                    )}
                  </div>
                  <span className="aw-group-count">{group.taskCount}</span>
                </div>

                {/* Column headers + rows */}
                {isExpanded && (
                  <>
                    <div className="aw-col-headers">
                      <div className="aw-col-header" />
                      <div className="aw-col-header">Key</div>
                      <div className="aw-col-header">Title</div>
                      <div className="aw-col-header">Status</div>
                      <div className="aw-col-header">Priority</div>
                      <div className="aw-col-header">Assignee</div>
                      <div className="aw-col-header">Sprint</div>
                    </div>

                    {group.taskCount === 0 ? (
                      <div className="aw-empty-row">No issues</div>
                    ) : (
                      group.tasks.map(task => {
                        if (task.depth > 0 && !expandedParentIds.has(task.parent_id)) return null;
                        const isParent = task.depth === 0 && task.children && task.children.length > 0;
                        const parentExpanded = expandedParentIds.has(task.id);
                        return (
                        <div
                          key={task.id}
                          className={`aw-issue-row${task.depth > 0 ? ' aw-issue-row--child' : ''}`}
                          onClick={() => handleRowClick(task)}
                          onContextMenu={(e) => handleContextMenu(e, task)}
                        >
                          {/* Type — chevron for expandable parents, spacer for others; icon always shown */}
                          <div className="aw-cell aw-cell-type">
                            {isParent ? (
                              <button
                                className="aw-expand-btn"
                                onClick={(e) => { e.stopPropagation(); toggleParent(task.id); }}
                              >
                                <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor"
                                     style={{ transform: parentExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease' }}>
                                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                                </svg>
                              </button>
                            ) : (
                              <span className="aw-expand-spacer" />
                            )}
                            <TicketTypeIcon type={task.task_type} size="sm" />
                          </div>

                          {/* Key */}
                          <div className="aw-cell">
                            <span className="aw-key">{task.task_key}</span>
                          </div>

                          {/* Title — indent for children */}
                          <div className="aw-cell" style={task.depth > 0 ? { paddingLeft: 20 } : {}}>
                            <span className="aw-title">{task.title}</span>
                          </div>

                          {/* Status */}
                          <div className="aw-cell">
                            <StatusBadge status={task.status} workflowStatuses={workflowStatuses} />
                          </div>

                          {/* Priority */}
                          <div className="aw-cell">
                            <div className="aw-priority">
                              <PriorityDot priority={task.priority} />
                              <span className="aw-priority-label">
                                {PRIORITY_CONFIG[task.priority]?.label || task.priority || '—'}
                              </span>
                            </div>
                          </div>

                          {/* Assignee */}
                          <div className="aw-cell">
                            {task.assignee ? (
                              <div className="aw-assignee">
                                <Avatar user={task.assignee} size={20} />
                                <span className="aw-assignee-name">{task.assignee.full_name}</span>
                              </div>
                            ) : (
                              <span className="aw-unassigned">Unassigned</span>
                            )}
                          </div>

                          {/* Sprint */}
                          <div className="aw-cell">
                            {task.sprint_id && sprintMap[task.sprint_id] ? (
                              <span className={`aw-sprint-pill aw-sprint-pill--${sprintMap[task.sprint_id].status}`}>
                                {sprintMap[task.sprint_id].name}
                              </span>
                            ) : (
                              <span className="aw-sprint-pill aw-sprint-pill--backlog">Backlog</span>
                            )}
                          </div>
                        </div>
                        );
                      })
                    )}
                  </>
                )}
              </div>
            );
          })
        )}
      </div>
      {contextMenu.visible && contextMenu.task && (
        <div
          ref={contextMenuRef}
          className="task-ctx-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            className="task-ctx-item"
            onClick={() => {
              window.open(`/tasks/${contextMenu.task.task_key}`, '_blank');
              setContextMenu(m => ({ ...m, visible: false }));
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            Open in new tab
          </button>
        </div>
      )}
    </div>
  );
};

export default AllWork;
